import { QueryClient } from "@tanstack/react-query";
import { apiQuery } from "utils/queries";

export type VocabularyPracticeSummary = {
  correct: number;
  total: number;
  practiced_at: string;
};

export type VocabularyBreakdown = {
  mastered: number;
  improving: number;
  needs_practice: number;
  untested: number;
};

export type VocabularyStats = {
  saved_words: number;
  saved_words_last_7_days: number;
  // Overdue per spaced repetition, plus every never-practiced word.
  due_today: number;
  breakdown: VocabularyBreakdown;
  last_practice: VocabularyPracticeSummary | null;
  // The newest words with each status, one practice round's worth.
  preview: {
    needs_practice: VocabularyWord[];
    improving: VocabularyWord[];
  };
};

export const vocabularyStatsQuery = apiQuery<VocabularyStats>({
  key: "/vocabulary/stats",
});

export type PracticeStatus = "mastered" | "improving" | "needs_practice";
export type WordStatus = PracticeStatus | "untested";

export type VocabularyWordPractice = {
  saved_word_id: string;
  attempts: number;
  correct: number;
  streak: number;
  last_practiced_at: string;
  status: PracticeStatus;
  recent: boolean[];
  due: boolean;
  next_due: string | null;
};

type VocabularyPracticeSummaryResponse = {
  list: VocabularyWordPractice[];
};

export const vocabularyPracticeSummaryQuery = apiQuery<VocabularyPracticeSummaryResponse>(
  {
    key: "/vocabulary/practice-summary",
  }
);

export type VocabularyWord = {
  id: string;
  word: string;
  pos: string | null;
  notes: string | null;
  definition: string | null;
  translation: string | null;
  audio: string | null;
  created: string;
  updated: string | null;
  status: WordStatus;
  attempts: number;
  correct: number;
  streak: number;
  last_practiced_at: string | null;
  recent: boolean[];
  due: boolean;
  next_due: string | null;
};

type GetVocabularyWordsParams = {
  page?: number;
  page_size?: number;
  status?: WordStatus | null;
  filter?: string | null;
  order_by?: "created" | "updated" | "word" | null;
  sort?: "asc" | "desc";
};

export type GetVocabularyWordsResponse = {
  list: VocabularyWord[];
  total: number;
};

export const vocabularyWordsQuery = apiQuery<
  GetVocabularyWordsResponse,
  GetVocabularyWordsParams
>({
  key: "/vocabulary/words",
});

export function practiceScorePercent(practice: VocabularyPracticeSummary) {
  return practice.total === 0 ? 0 : Math.round((practice.correct / practice.total) * 100);
}

export function invalidateVocabularyCaches(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: vocabularyStatsQuery.baseKey });
  void queryClient.invalidateQueries({ queryKey: vocabularyWordsQuery.baseKey });
  void queryClient.invalidateQueries({
    queryKey: vocabularyPracticeSummaryQuery.baseKey,
  });
}
